import { DateTime } from 'luxon';
import { ZONA_OPERATIVA } from '../tipos/comunes.js';
import { dentroDeHorario } from './horario.js';
import { extenderFin, seSolapan } from './rango.js';
function agrupar(items, clave) {
    const mapa = new Map();
    for (const item of items) {
        const k = clave(item);
        const lista = mapa.get(k);
        if (lista)
            lista.push(item);
        else
            mapa.set(k, [item]);
    }
    return mapa;
}
export function construirContexto(entrada, principalesPermitidos) {
    return {
        tipos: new Map(entrada.tipos.map((t) => [t.id, t])),
        recursos: new Map(entrada.recursos.map((r) => [r.id, r])),
        porTipo: agrupar(entrada.recursos.filter((r) => r.activo), (r) => r.tipoId),
        horarios: agrupar(entrada.horarios, (h) => h.recursoId),
        bloqueos: new Map([...agrupar(entrada.bloqueos, (b) => b.recursoId)].map(([id, bs]) => [
            id,
            bs.map((b) => b.rango),
        ])),
        ocupacion: new Map([...agrupar(entrada.ocupacion, (o) => o.recursoId)].map(([id, os]) => [
            id,
            os.map((o) => o.rango),
        ])),
        asignacionesEspacio: entrada.asignacionesEspacio ?? [],
        zona: entrada.zona ?? ZONA_OPERATIVA,
        recursoPreferido: entrada.recursoPreferido,
        principalesPermitidos,
        especialidadRequeridaId: entrada.servicio.especialidadRequeridaId,
    };
}
/** La asignación publicada que habilita exactamente esta pareja y todo su rango ocupado. */
/**
 * Parte un `daterange` de Postgres —`["2026-01-01","2026-06-01")`— en los dos
 * bordes que `AsignacionEspacio` necesita.
 *
 * Un borde superior vacio (`[2026-01-01,)`) significa "sin fin" y da `null`.
 * Vive aca porque lo necesitan la API al cargar el contexto y la PWA al armar
 * el suyo, y hacerlo inline con `split` encadenado deja accesos a indice sin
 * chequear.
 */
export function bordesDeVigencia(vigencia) {
    const limpio = (borde) => (borde ?? '')
        .replace(/[[\]()]/g, '')
        .replace(/"/g, '')
        .trim();
    const partes = vigencia.split(',');
    const hasta = limpio(partes[1]);
    return {
        vigenciaDesde: new Date(limpio(partes[0])),
        vigenciaHasta: hasta === '' ? null : new Date(hasta),
    };
}
/**
 * ?Esta asignacion —una plantilla semanal— cubre este tramo concreto?
 *
 * `asignacion_espacio` dejo de guardar un rango absoluto: hoy es un dia de la
 * semana, un `desde`/`hasta` de reloj y una `vigencia` de fechas. Materializarla
 * para un tramo concreto es esta comparacion, y vive aca una sola vez porque la
 * necesitan el motor, el drag del timeline y la vista de la agenda.
 *
 * Todo semiabierto: un tramo que termina exactamente a `hasta` esta cubierto;
 * uno que empieza a `hasta`, no. Igual con `vigencia`: `[desde, hasta)`.
 */
export function asignacionCubre(asignacion, tramo, zona = 'America/Asuncion') {
    const dtInicio = DateTime.fromJSDate(tramo.inicio, { zone: zona });
    const dtFin = DateTime.fromJSDate(tramo.fin, { zone: zona });
    // La columna `dia_semana` usa domingo = 0; Luxon usa domingo = 7.
    const diaDelTramo = dtInicio.weekday === 7 ? 0 : dtInicio.weekday;
    if (diaDelTramo !== asignacion.diaSemana)
        return false;
    // La vigencia se compara por fecha local, no por instante: una asignacion
    // vigente "desde el 1" cubre el tramo de las 08:00 del 1.
    const fechaDelTramo = dtInicio.startOf('day');
    if (fechaDelTramo < DateTime.fromJSDate(asignacion.vigenciaDesde, { zone: zona }).startOf('day')) {
        return false;
    }
    if (asignacion.vigenciaHasta !== null &&
        fechaDelTramo >= DateTime.fromJSDate(asignacion.vigenciaHasta, { zone: zona }).startOf('day')) {
        return false;
    }
    const minutos = (reloj) => {
        const [h, m] = reloj.split(':');
        if (h === undefined || m === undefined)
            return null;
        const total = Number(h) * 60 + Number(m);
        return Number.isFinite(total) ? total : null;
    };
    const desde = minutos(asignacion.desde);
    const hasta = minutos(asignacion.hasta);
    if (desde === null || hasta === null)
        return false;
    // El fin se proyecta sobre el dia de inicio: un tramo no cruza de dia.
    const inicioTramo = dtInicio.hour * 60 + dtInicio.minute;
    const finTramo = dtFin.hasSame(dtInicio, 'day') ? dtFin.hour * 60 + dtFin.minute : 24 * 60;
    return inicioTramo >= desde && finTramo <= hasta;
}
export function asignacionQueHabilita(ctx, personaId, espacioId, tramo, tramoServicio) {
    const espacio = ctx.recursos.get(espacioId);
    const tipoEspacio = espacio ? ctx.tipos.get(espacio.tipoId) : undefined;
    if (!tipoEspacio?.esEspacio)
        return undefined;
    const ocupado = rangoOcupado(tipoEspacio, tramo, tramoServicio);
    return ctx.asignacionesEspacio.find((a) => a.personaId === personaId &&
        a.espacioId === espacioId &&
        asignacionCubre(a, ocupado, ctx.zona));
}
/** §3.6 — el espacio se ocupa por el tramo mas el buffer; la persona, solo por el tramo. */
export function rangoOcupado(tipo, tramo, tramoServicio) {
    const bufferMin = tramoServicio?.bufferMin ?? tipo.bufferMin;
    return tipo.esPersona || bufferMin <= 0 ? tramo : extenderFin(tramo, bufferMin);
}
/**
 * ¿El recurso puede tomar este tramo?
 *
 * El horario se evalua contra el tramo de trabajo, no contra el rango con
 * buffer: la limpieza posterior no tiene por que caer dentro del horario de
 * atencion, y si lo exigieramos se perderia el ultimo turno de cada dia.
 */
export function recursoDisponible(ctx, recursoId, tramo, tramoServicio) {
    const recurso = ctx.recursos.get(recursoId);
    if (!recurso || !recurso.activo)
        return false;
    const tipo = ctx.tipos.get(recurso.tipoId);
    if (!tipo)
        return false;
    let horarios = ctx.horarios.get(recursoId) ?? [];
    if (tipo.esEspacio) {
        const tramoDateInicio = new Date(tramo.inicio.getTime());
        tramoDateInicio.setUTCHours(0, 0, 0, 0);
        const asignacionesVigentes = ctx.asignacionesEspacio.filter((a) => {
            if (a.espacioId !== recursoId)
                return false;
            const dtVigenciaDesde = new Date(a.vigenciaDesde);
            dtVigenciaDesde.setUTCHours(0, 0, 0, 0);
            if (tramoDateInicio < dtVigenciaDesde)
                return false;
            if (a.vigenciaHasta) {
                const dtVigenciaHasta = new Date(a.vigenciaHasta);
                dtVigenciaHasta.setUTCHours(0, 0, 0, 0);
                if (tramoDateInicio >= dtVigenciaHasta)
                    return false;
            }
            return true;
        });
        horarios = asignacionesVigentes.map((a) => ({
            recursoId, diaSemana: a.diaSemana,
            desde: a.desde,
            hasta: a.hasta,
        }));
    }
    if (!dentroDeHorario(horarios, tramo, ctx.zona))
        return false;
    const conBuffer = rangoOcupado(tipo, tramo, tramoServicio);
    for (const bloqueo of ctx.bloqueos.get(recursoId) ?? []) {
        if (seSolapan(bloqueo, tramo))
            return false;
    }
    for (const ocupado of ctx.ocupacion.get(recursoId) ?? []) {
        if (seSolapan(ocupado, conBuffer))
            return false;
    }
    return true;
}
/**
 * §5 de la tarea — entre recursos equivalentes se elige el que menos fragmenta
 * la agenda: el que ya tiene citas cerca, no el primero libre. Es lo que sostiene
 * el argumento de venta de ocupacion.
 *
 * Menor distancia al vecino mas cercano = mejor. Un recurso vacio devuelve
 * Infinity y queda ultimo.
 */
export function distanciaAlVecino(ctx, recursoId, tramo) {
    let minima = Number.POSITIVE_INFINITY;
    for (const ocupado of ctx.ocupacion.get(recursoId) ?? []) {
        const distancia = ocupado.fin.getTime() <= tramo.inicio.getTime()
            ? tramo.inicio.getTime() - ocupado.fin.getTime()
            : ocupado.inicio.getTime() - tramo.fin.getTime();
        if (distancia >= 0 && distancia < minima)
            minima = distancia;
    }
    return minima === Number.POSITIVE_INFINITY ? minima : minima / 60_000;
}
function libreEnEsteServicio(estado, recursoId, tramo) {
    return !(estado.propios.get(recursoId) ?? []).some((r) => seSolapan(r, tramo));
}
function candidatos(ctx, req, tramo, estado, yaEnTramo, personasDelTramo, tramoServicio) {
    const tipo = ctx.tipos.get(req.recursoTipoId);
    if (!tipo)
        return [];
    // Continuidad: hay que reusar la instancia exacta del tramo referido (§3.3).
    if (req.mismoRecursoQueTramo !== undefined) {
        const previos = estado.porTramo.get(req.mismoRecursoQueTramo)?.get(req.recursoTipoId) ?? [];
        return previos.filter((id) => !yaEnTramo.includes(id) &&
            recursoDisponible(ctx, id, tramo, tramoServicio) &&
            libreEnEsteServicio(estado, id, tramo));
    }
    const posibles = (ctx.porTipo.get(req.recursoTipoId) ?? []).filter((recurso) => {
        if (yaEnTramo.includes(recurso.id))
            return false;
        if (req.capacidadRequerida && !(recurso.capacidades ?? []).includes(req.capacidadRequerida)) {
            return false;
        }
        if (tipo.esEspacio) {
            // En un tramo clínico, la afinidad sólo ordena: la autorización efectiva
            // es una asignación publicada que cubra también el buffer del espacio.
            const principales = personasDelTramo.filter((personaId) => {
                const persona = ctx.recursos.get(personaId);
                return persona ? ctx.tipos.get(persona.tipoId)?.seleccionablePorCliente === true : false;
            });
            if (!principales.some((personaId) => asignacionQueHabilita(ctx, personaId, recurso.id, tramo, tramoServicio)))
                return false;
        }
        if (tipo.seleccionablePorCliente) {
            if (ctx.recursoPreferido && recurso.id !== ctx.recursoPreferido)
                return false;
            if (ctx.principalesPermitidos && !ctx.principalesPermitidos.has(recurso.id))
                return false;
            if (tipo.esPersona && ctx.especialidadRequeridaId && !(recurso.especialidades ?? []).includes(ctx.especialidadRequeridaId))
                return false;
        }
        if (!recursoDisponible(ctx, recurso.id, tramo, tramoServicio))
            return false;
        return libreEnEsteServicio(estado, recurso.id, tramo);
    });
    return posibles
        .map((recurso) => ({
        id: recurso.id,
        distancia: distanciaAlVecino(ctx, recurso.id, tramo),
    }))
        .sort((a, b) => a.distancia - b.distancia || a.id.localeCompare(b.id))
        .map((c) => c.id);
}
/** Los requisitos de persona se resuelven primero: de ellos depende que espacios son legales. */
function ordenarRequisitos(ctx, reqs) {
    return [...reqs].sort((a, b) => {
        const pa = ctx.tipos.get(a.recursoTipoId)?.esPersona ? 0 : 1;
        const pb = ctx.tipos.get(b.recursoTipoId)?.esPersona ? 0 : 1;
        return pa - pb;
    });
}
function resolverTramo(ctx, reqs, indice, tramo, estado, elegidos, tramoServicio) {
    const req = reqs[indice];
    if (!req)
        return combinacionClinicaValida(ctx, elegidos, tramo, tramoServicio);
    const yaEnTramo = [...elegidos.values()].flat();
    const personasDelTramo = [...elegidos.entries()]
        .filter(([tipoId]) => ctx.tipos.get(tipoId)?.esPersona)
        .flatMap(([, ids]) => ids);
    const disponibles = candidatos(ctx, req, tramo, estado, yaEnTramo, personasDelTramo, tramoServicio);
    const cantidad = Math.max(1, req.cantidad);
    if (disponibles.length < cantidad)
        return false;
    // Combinaciones de `cantidad` recursos, en el orden ya priorizado.
    const combinar = (desde, tomados) => {
        if (tomados.length === cantidad) {
            const previos = elegidos.get(req.recursoTipoId) ?? [];
            elegidos.set(req.recursoTipoId, [...previos, ...tomados]);
            if (resolverTramo(ctx, reqs, indice + 1, tramo, estado, elegidos, tramoServicio))
                return true;
            if (previos.length === 0)
                elegidos.delete(req.recursoTipoId);
            else
                elegidos.set(req.recursoTipoId, previos);
            return false;
        }
        for (let i = desde; i < disponibles.length; i++) {
            const id = disponibles[i];
            if (!id)
                continue;
            if (combinar(i + 1, [...tomados, id]))
                return true;
        }
        return false;
    };
    return combinar(0, []);
}
function combinacionClinicaValida(ctx, elegidos, tramo, tramoServicio) {
    const ids = [...elegidos.values()].flat();
    const espacios = ids.filter((id) => {
        const recurso = ctx.recursos.get(id);
        return recurso ? ctx.tipos.get(recurso.tipoId)?.esEspacio === true : false;
    });
    if (espacios.length === 0)
        return true;
    const principales = ids.filter((id) => {
        const recurso = ctx.recursos.get(id);
        const tipo = recurso ? ctx.tipos.get(recurso.tipoId) : undefined;
        return tipo?.esPersona === true && tipo.seleccionablePorCliente;
    });
    if (principales.length !== 1)
        return false;
    return espacios.some((espacioId) => asignacionQueHabilita(ctx, principales[0], espacioId, tramo, tramoServicio));
}
/**
 * Asigna recursos a todos los tramos de un servicio que arranca en `inicio`.
 * Devuelve `null` si no hay ninguna combinacion valida.
 */
export function asignarRecursos(entrada, inicio, principalesPermitidos) {
    const ctx = construirContexto(entrada, principalesPermitidos);
    const tramos = [...entrada.servicio.tramos].sort((a, b) => a.orden - b.orden);
    const estado = { propios: new Map(), porTramo: new Map() };
    const asignaciones = [];
    let cursor = inicio;
    const rangos = tramos.map((tramo) => {
        const rango = { inicio: cursor, fin: new Date(cursor.getTime() + tramo.duracionMin * 60_000) };
        cursor = rango.fin;
        return rango;
    });
    const avanzar = (indice) => {
        const tramoServicio = tramos[indice];
        const rango = rangos[indice];
        if (!tramoServicio || !rango)
            return true;
        const elegidos = new Map();
        const reqs = ordenarRequisitos(ctx, tramoServicio.requisitos);
        const intentar = () => {
            if (!resolverTramo(ctx, reqs, 0, rango, estado, elegidos, tramoServicio))
                return false;
            const ids = [...elegidos.values()].flat();
            for (const id of ids) {
                const previos = estado.propios.get(id) ?? [];
                estado.propios.set(id, [...previos, rango]);
            }
            estado.porTramo.set(tramoServicio.orden, new Map(elegidos));
            const principales = ids.filter((id) => {
                const tipo = ctx.tipos.get(ctx.recursos.get(id).tipoId);
                return tipo?.esPersona === true && tipo.seleccionablePorCliente;
            });
            const espacios = ids.filter((id) => ctx.tipos.get(ctx.recursos.get(id).tipoId)?.esEspacio === true);
            const principal = principales.length === 1 ? principales[0] : undefined;
            const pareja = principal
                ? espacios
                    .map((espacioId) => asignacionQueHabilita(ctx, principal, espacioId, rango, tramoServicio))
                    .find((a) => a !== undefined)
                : undefined;
            asignaciones.push({
                orden: tramoServicio.orden,
                rango,
                recursos: ids.map((id) => ({
                    recursoId: id,
                    tipoId: ctx.recursos.get(id).tipoId,
                    rango,
                })),
                ...(pareja && principal
                    ? {
                        asignacionEspacioId: pareja.id,
                        profesionalPrincipalId: principal,
                    }
                    : {}),
            });
            if (avanzar(indice + 1))
                return true;
            // Backtrack: se deshace este tramo y se prueba otra combinacion.
            asignaciones.pop();
            estado.porTramo.delete(tramoServicio.orden);
            for (const id of ids) {
                const previos = estado.propios.get(id) ?? [];
                estado.propios.set(id, previos.filter((r) => r !== rango));
            }
            return false;
        };
        return intentar();
    };
    if (!avanzar(0))
        return null;
    return materializarRangos(asignaciones, tramos, Array.from(ctx.tipos.values()));
}
/**
 * Normaliza los rangos de recursos al rango del tramo, y aplica el buffer al final del
 * ultimo tramo consecutivo en que el espacio se usa. Un mismo espacio usado en tramos
 * contiguos de la misma cita no se limpia en el medio: es el mismo paciente.
 */
export function materializarRangos(asignacion, servicioTramos, tipos) {
    const tiposMap = new Map(tipos.map((t) => [t.id, t]));
    const ordenados = [...asignacion].sort((a, b) => a.orden - b.orden);
    const normalizados = ordenados.map((tramo) => ({
        ...tramo,
        recursos: tramo.recursos.map((r) => ({ ...r, rango: tramo.rango })),
    }));
    return normalizados.map((tramo, i) => {
        const tramoServicio = servicioTramos.find((t) => t.orden === tramo.orden);
        return {
            ...tramo,
            recursos: tramo.recursos.map((recurso) => {
                const tipo = tiposMap.get(recurso.tipoId);
                if (!tipo || tipo.esPersona)
                    return recurso;
                const bufferMin = tramoServicio?.bufferMin ?? tipo.bufferMin;
                if (bufferMin <= 0)
                    return recurso;
                const siguiente = normalizados[i + 1];
                const sigueUsandolo = siguiente !== undefined &&
                    siguiente.rango.inicio.getTime() === tramo.rango.fin.getTime() &&
                    siguiente.recursos.some((r) => r.recursoId === recurso.recursoId);
                return sigueUsandolo
                    ? recurso
                    : { ...recurso, rango: extenderFin(tramo.rango, bufferMin) };
            }),
        };
    });
}
//# sourceMappingURL=asignacion.js.map